import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { MobileNavigation } from '../components/MobileNavigation';
import { FileText, CheckCircle, Warning, Clock, Download, ArrowRight, Shield, User, CalendarCheck } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function Waivers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [waiverTemplate, setWaiverTemplate] = useState(null);
  const [playerWaivers, setPlayerWaivers] = useState({});
  
  // Waiver signing flow state
  const [signingPlayer, setSigningPlayer] = useState(null);
  const [step, setStep] = useState(1); // 1: Read Waiver, 2: Consent, 3: Sign, 4: Confirmation
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // COPPA Consent checkboxes
  const [isParentGuardian, setIsParentGuardian] = useState(false);
  const [consentDataCollection, setConsentDataCollection] = useState(false);
  const [consentTermsPrivacy, setConsentTermsPrivacy] = useState(false);
  const [consentPhotoVideo, setConsentPhotoVideo] = useState(false);
  
  const waiverScrollRef = useRef(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      // Load waiver template
      const waiverResponse = await fetch(`${getApiBaseUrl()}/api/waivers/template`);
      if (waiverResponse.ok) {
        const waiverData = await waiverResponse.json();
        setWaiverTemplate(waiverData);
      }
      
      // Load players
      const playersResponse = await fetch(`${getApiBaseUrl()}/api/players`, {
        credentials: 'include'
      });
      if (playersResponse.ok) {
        const playersData = await playersResponse.json();
        setPlayers(playersData);
        
        // Load waiver status for each player
        const waiverStatuses = {};
        for (const player of playersData) {
          const statusResponse = await fetch(
            `${getApiBaseUrl()}/api/waivers/player/${player.player_id}/status`,
            { credentials: 'include' }
          );
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            waiverStatuses[player.player_id] = statusData;
          }
        }
        setPlayerWaivers(waiverStatuses);
      }
    } catch (error) {
      console.error('Load waivers error:', error);
      toast.error('Failed to load waiver data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Consider scrolled to bottom if within 50px of the end
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setHasScrolledToBottom(true);
    }
  }, []);
  
  const startWaiverFlow = (player) => {
    setSigningPlayer(player);
    setStep(1);
    setHasScrolledToBottom(false);
    setSignedName('');
    setIsParentGuardian(false);
    setConsentDataCollection(false);
    setConsentTermsPrivacy(false);
    setConsentPhotoVideo(false);
  };
  
  const cancelWaiverFlow = () => {
    setSigningPlayer(null);
    setStep(1);
    setHasScrolledToBottom(false);
    setSignedName('');
    setIsParentGuardian(false);
    setConsentDataCollection(false);
    setConsentTermsPrivacy(false);
    setConsentPhotoVideo(false);
  };
  
  const proceedToConsent = () => {
    if (!hasScrolledToBottom) {
      toast.error('Please scroll to the bottom of the waiver to continue');
      return;
    }
    setStep(2);
  };
  
  const proceedToSign = () => {
    if (!isParentGuardian || !consentDataCollection || !consentTermsPrivacy || !consentPhotoVideo) {
      toast.error('Please check all required consent boxes');
      return;
    }
    setStep(3);
  };
  
  const handleSignWaiver = async () => {
    if (!signedName.trim() || signedName.trim().length < 2) {
      toast.error('Please enter your full legal name');
      return;
    }
    
    if (!isParentGuardian || !consentDataCollection || !consentTermsPrivacy || !consentPhotoVideo) {
      toast.error('All consent checkboxes must be checked');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/waivers/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          player_id: signingPlayer.player_id,
          signed_name: signedName.trim(),
          is_parent_guardian: isParentGuardian,
          consent_data_collection: consentDataCollection,
          consent_terms_privacy: consentTermsPrivacy,
          consent_photo_video: consentPhotoVideo
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        setPlayerWaivers(prev => ({
          ...prev,
          [signingPlayer.player_id]: {
            signed: true,
            expired: false,
            valid: true,
            status: 'active',
            expires_at: result.expires_at,
            signed_at: new Date().toISOString(),
            consent_given: true
          }
        }));
        
        // Move to confirmation step
        setStep(4);
        toast.success('Waiver & Parental Consent Complete!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to sign waiver');
      }
    } catch (error) {
      console.error('Sign waiver error:', error);
      toast.error('Failed to sign waiver. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>Stay On Hoops - Waiver & Parental Consent</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
            h1 { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
            h2 { margin-top: 30px; }
            .section { margin: 20px 0; padding: 15px; border: 1px solid #ccc; }
            .signature { margin-top: 40px; border-top: 1px solid #000; padding-top: 20px; }
            .consent-item { margin: 10px 0; padding-left: 20px; }
          </style>
        </head>
        <body>
          <h1>STAY ON HOOPS - WAIVER & PARENTAL CONSENT</h1>
          <p><strong>Player:</strong> ${signingPlayer?.name || '[Player Name]'}</p>
          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
          
          <h2>WAIVER TEXT</h2>
          <div class="section">
            ${waiverTemplate?.content?.replace(/\n/g, '<br>') || 'Waiver content not available'}
          </div>
          
          <h2>PARENTAL CONSENT & ACKNOWLEDGMENT</h2>
          <div class="section">
            <p>By signing below, I confirm that:</p>
            <div class="consent-item">☑ I am the parent or legal guardian of the child being registered.</div>
            <div class="consent-item">☑ I provide verifiable parental consent for Intuitive Mentors Organization, through its Stay On Hoops program, to collect and use my child's information solely for program operations.</div>
            <div class="consent-item">☑ I agree to the Waiver, Terms & Conditions, and Privacy Policy.</div>
          </div>
          
          <div class="signature">
            <p><strong>Parent/Guardian Signature:</strong> ________________________________</p>
            <p><strong>Printed Name:</strong> ________________________________</p>
            <p><strong>Date:</strong> ________________________________</p>
          </div>
          
          <p style="margin-top: 40px; font-size: 12px; color: #666;">
            Stay On Hoops is a product of Intuitive Mentors Organization.<br>
            This document complies with COPPA and California CCPA/CPRA requirements.
          </p>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };
  
  const downloadWaiverPdf = async (playerId) => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/waivers/player/${playerId}/pdf`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'waiver.pdf';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Waiver PDF downloaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to download waiver PDF');
      }
    } catch (error) {
      console.error('Download PDF error:', error);
      toast.error('Failed to download waiver PDF');
    }
  };
  
  const getWaiverStatus = (playerId) => {
    const status = playerWaivers[playerId];
    if (!status) return { icon: Clock, color: 'text-gray-500', text: 'Loading...', bg: 'bg-gray-500/20' };
    
    if (!status.signed || status.status === 'missing') {
      return { 
        icon: Warning, 
        color: 'text-red-500', 
        text: 'Not Signed', 
        bg: 'bg-red-500/20',
        action: 'sign',
        message: 'Waiver and parental consent required before participation'
      };
    }
    
    if (status.expired || status.status === 'expired') {
      return { 
        icon: Clock, 
        color: 'text-yellow-500', 
        text: 'Expired', 
        bg: 'bg-yellow-500/20',
        action: 'renew',
        message: 'Waiver expired - please sign again'
      };
    }
    
    return { 
      icon: CheckCircle, 
      color: 'text-green-500', 
      text: 'Active', 
      bg: 'bg-green-500/20',
      expiresAt: status.expires_at,
      message: 'Waiver and consent active'
    };
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  // Waiver Signing Flow
  if (signingPlayer) {
    return (
      <div className="min-h-screen bg-background pb-24" data-testid="waiver-signing-flow">
        {/* Premium Progress Header */}
        <div className="glass-effect sticky top-0 z-40">
          <div className="px-6 py-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Step {step} of 4</p>
                <h1 className="text-xl font-black uppercase tracking-tight">Waiver & Consent</h1>
              </div>
              <Button variant="ghost" size="sm" onClick={cancelWaiverFlow}>Cancel</Button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">For: <span className="text-primary font-medium">{signingPlayer.name}</span></p>
            
            {/* Step Progress - Premium */}
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div className={`w-full h-2 rounded-full transition-all ${
                    step >= s ? 'bg-primary' : 'bg-muted'
                  }`} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2 uppercase tracking-wide">
              <span className={step >= 1 ? 'text-primary' : ''}>Read</span>
              <span className={step >= 2 ? 'text-primary' : ''}>Consent</span>
              <span className={step >= 3 ? 'text-primary' : ''}>Sign</span>
              <span className={step >= 4 ? 'text-primary' : ''}>Done</span>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-8">
          {/* Step 1: Read Waiver */}
          {step === 1 && (
            <div className="space-y-4" data-testid="waiver-step-1">
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <FileText size={20} weight="duotone" />
                      WAIVER AGREEMENT
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-waiver-btn">
                      <Download size={16} className="mr-1" /> Print
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Please read the entire waiver. You must scroll to the bottom to continue.
                  </p>
                </CardHeader>
                <CardContent>
                  <div 
                    ref={waiverScrollRef}
                    onScroll={handleScroll}
                    className="h-80 overflow-y-auto border border-border rounded-lg p-4 bg-muted/30 text-sm leading-relaxed"
                    data-testid="waiver-scroll-container"
                  >
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      {waiverTemplate?.content ? (
                        waiverTemplate.content.split('\n').map((paragraph, idx) => (
                          <p key={idx} className={paragraph.startsWith('**') ? 'font-bold mt-4' : 'mt-2'}>
                            {paragraph.replace(/\*\*/g, '')}
                          </p>
                        ))
                      ) : (
                        <p>Loading waiver content...</p>
                      )}
                    </div>
                    
                    {/* Scroll indicator at bottom */}
                    <div className="mt-6 pt-4 border-t border-border text-center">
                      <p className="text-xs text-muted-foreground">
                        — End of Waiver Document —
                      </p>
                    </div>
                  </div>
                  
                  {!hasScrolledToBottom && (
                    <p className="text-sm text-yellow-500 mt-3 flex items-center gap-2">
                      <Warning size={16} />
                      Scroll to the bottom of the waiver to continue
                    </p>
                  )}
                </CardContent>
              </Card>
              
              <Button 
                onClick={proceedToConsent} 
                disabled={!hasScrolledToBottom}
                className="w-full btn-primary"
                data-testid="proceed-to-consent-btn"
              >
                Continue to Parental Consent <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          )}
          
          {/* Step 2: COPPA Parental Consent */}
          {step === 2 && (
            <div className="space-y-4" data-testid="waiver-step-2">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Shield size={20} weight="duotone" className="text-primary" />
                    PARENTAL CONSENT & ACKNOWLEDGMENT
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-lg text-sm leading-relaxed">
                    <p className="font-medium mb-3">By signing below, I confirm that:</p>
                    <ul className="space-y-2 list-disc pl-5">
                      <li>I am the parent or legal guardian of the child being registered.</li>
                      <li>I provide verifiable parental consent for Intuitive Mentors Organization, through its Stay On Hoops program, to collect and use my child&apos;s information solely for program operations, including registration, session management, safety, communication, and waiver compliance.</li>
                      <li>I understand that Stay On Hoops is an organized pickup basketball program operated by Intuitive Mentors Organization and is non-refereed.</li>
                      <li>I acknowledge that participation in basketball involves inherent physical risks and voluntarily allow my child to participate.</li>
                      <li>I understand that my child may be removed from a session for safety or conduct reasons.</li>
                      <li>I have read and agree to the Waiver, Terms & Conditions, and Privacy Policy.</li>
                    </ul>
                    
                    <p className="font-medium mt-4 mb-3">Photography & Video Recording:</p>
                    <ul className="space-y-2 list-disc pl-5">
                      <li>I understand that photographs and/or video recordings may be taken during Stay On Hoops sessions and events.</li>
                      <li>I grant permission for my child&apos;s image, likeness, and/or voice to be captured in photographs, videos, or other media.</li>
                      <li>I authorize Stay On Hoops and Intuitive Mentors Organization to use such media for promotional purposes, including but not limited to: website content, social media, marketing materials, and program documentation.</li>
                      <li>I understand this consent is given without expectation of compensation and that I waive any right to inspect or approve the finished materials.</li>
                      <li>I understand I may revoke this media consent at any time by contacting info@stayonhoops.com, though previously published materials may not be recalled.</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-4 pt-4">
                    <p className="text-sm font-medium text-foreground">
                      Required Consent (all must be checked):
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                        <Checkbox 
                          id="parent-guardian" 
                          checked={isParentGuardian}
                          onCheckedChange={setIsParentGuardian}
                          data-testid="checkbox-parent-guardian"
                        />
                        <Label htmlFor="parent-guardian" className="text-sm cursor-pointer leading-relaxed">
                          I am the parent or legal guardian of the registered child
                        </Label>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                        <Checkbox 
                          id="consent-data" 
                          checked={consentDataCollection}
                          onCheckedChange={setConsentDataCollection}
                          data-testid="checkbox-consent-data"
                        />
                        <Label htmlFor="consent-data" className="text-sm cursor-pointer leading-relaxed">
                          I consent to the collection and use of my child&apos;s information as described above
                        </Label>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                        <Checkbox 
                          id="consent-terms" 
                          checked={consentTermsPrivacy}
                          onCheckedChange={setConsentTermsPrivacy}
                          data-testid="checkbox-consent-terms"
                        />
                        <Label htmlFor="consent-terms" className="text-sm cursor-pointer leading-relaxed">
                          I agree to the Waiver, Terms & Conditions, and Privacy Policy
                        </Label>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                        <Checkbox 
                          id="consent-photo-video" 
                          checked={consentPhotoVideo}
                          onCheckedChange={setConsentPhotoVideo}
                          data-testid="checkbox-consent-photo-video"
                        />
                        <Label htmlFor="consent-photo-video" className="text-sm cursor-pointer leading-relaxed">
                          I consent to photography and video recording of my child as described above, and authorize the use of such media for promotional and program purposes
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={proceedToSign} 
                  disabled={!isParentGuardian || !consentDataCollection || !consentTermsPrivacy || !consentPhotoVideo}
                  className="flex-1 btn-primary"
                  data-testid="proceed-to-sign-btn"
                >
                  Continue to Sign <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 3: Electronic Signature */}
          {step === 3 && (
            <div className="space-y-4" data-testid="waiver-step-3">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <User size={20} weight="duotone" className="text-primary" />
                    ELECTRONIC SIGNATURE
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                    <p className="text-sm">
                      By typing your full legal name below, you are providing your electronic signature, 
                      which has the same legal effect as a handwritten signature.
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signed-name" className="text-sm font-medium">
                        Parent/Guardian Full Legal Name *
                      </Label>
                      <Input
                        id="signed-name"
                        type="text"
                        placeholder="Enter your full legal name"
                        value={signedName}
                        onChange={(e) => setSignedName(e.target.value)}
                        className="text-lg"
                        data-testid="signed-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Date</Label>
                      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                        <CalendarCheck size={18} className="text-muted-foreground" />
                        <span className="text-sm">{new Date().toLocaleDateString('en-US', { 
                          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                        })}</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground">
                      <p>Your IP address will be recorded for verification purposes.</p>
                    </div>
                  </div>
                  
                  {/* Summary */}
                  <div className="pt-4 border-t border-border space-y-2">
                    <p className="text-sm font-medium">You are signing for:</p>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="font-bold">{signingPlayer.name}</p>
                      <p className="text-sm text-muted-foreground">Age: {signingPlayer.age}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                  Back
                </Button>
                <Button 
                  onClick={handleSignWaiver} 
                  disabled={!signedName.trim() || signedName.trim().length < 2 || submitting}
                  className="flex-1 btn-primary"
                  data-testid="sign-submit-btn"
                >
                  {submitting ? <LoadingSpinner size="sm" /> : 'Sign & Submit'}
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-6" data-testid="waiver-step-4">
              <Card className="bg-card border-border">
                <CardContent className="pt-8 text-center">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={48} weight="fill" className="text-green-500" />
                  </div>
                  
                  <h2 className="text-2xl font-heading mb-2">WAIVER & PARENTAL CONSENT COMPLETE</h2>
                  <p className="text-muted-foreground mb-6">
                    Thank you for completing the waiver and parental consent for {signingPlayer.name}.
                  </p>
                  
                  <div className="p-4 bg-muted/50 rounded-lg text-left space-y-2 mb-6">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Player:</span>
                      <span className="text-sm font-medium">{signingPlayer.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Signed By:</span>
                      <span className="text-sm font-medium">{signedName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Date:</span>
                      <span className="text-sm font-medium">{new Date().toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Status:</span>
                      <span className="text-sm font-medium text-green-500">Active</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Expires:</span>
                      <span className="text-sm font-medium">
                        {formatDate(playerWaivers[signingPlayer.player_id]?.expires_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Download PDF Button */}
                  <Button
                    variant="outline"
                    onClick={() => downloadWaiverPdf(signingPlayer.player_id)}
                    className="mb-4"
                    data-testid="download-pdf-confirmation-btn"
                  >
                    <Download size={16} className="mr-2" /> Download Signed Waiver PDF
                  </Button>
                  
                  <p className="text-sm text-muted-foreground mb-6">
                    {signingPlayer.name} is now eligible to register for and participate in sessions.
                  </p>
                </CardContent>
              </Card>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline" 
                  onClick={cancelWaiverFlow}
                  className="flex-1"
                >
                  Back to Waivers
                </Button>
                <Button 
                  onClick={() => navigate('/sessions')}
                  className="flex-1 btn-primary"
                  data-testid="go-to-sessions-btn"
                >
                  Register for Sessions <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
        
        <MobileNavigation userRole="parent" />
      </div>
    );
  }
  
  // Main Waivers List View
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="waivers-page">
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Required</p>
          <h1 className="text-2xl font-black uppercase tracking-tight">Waivers & Consent</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Complete before registering for sessions
          </p>
        </div>
      </div>
      
      {/* Important Notice */}
      <div className="px-6 pt-6">
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex gap-3">
          <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center text-yellow-500 shrink-0">
            <Warning size={20} weight="duotone" />
          </div>
          <div>
            <p className="font-heading text-sm text-yellow-500">IMPORTANT</p>
            <p className="text-sm text-muted-foreground">
              A signed waiver and parental consent is required before your child can register for or participate in any session.
            </p>
          </div>
        </div>
      </div>
      
      {/* Players Waiver Status */}
      <div className="px-6 py-8 space-y-4 stagger-children">
        {players.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <FileText size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO PLAYERS REGISTERED</h3>
              <p className="text-muted-foreground mb-4">
                Register a player first to manage their waivers
              </p>
              <Button onClick={() => navigate('/players')} className="btn-primary">
                Register a Player
              </Button>
            </CardContent>
          </Card>
        ) : (
          players.map((player) => {
            const status = getWaiverStatus(player.player_id);
            const StatusIcon = status.icon;
            
            return (
              <Card key={player.player_id} className="bg-card border-border card-hover" data-testid={`waiver-card-${player.player_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${status.bg}`}>
                        <StatusIcon size={24} weight="duotone" className={status.color} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-heading text-lg">{player.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${status.bg} ${status.color}`}>
                            {status.text}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">Age: {player.age}</p>
                        
                        {status.expiresAt && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Expires: {formatDate(status.expiresAt)}
                          </p>
                        )}
                        
                        {status.message && (
                          <p className={`text-sm mt-2 ${status.action ? 'text-yellow-500' : 'text-green-500'}`}>
                            {status.message}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {(status.action === 'sign' || status.action === 'renew') ? (
                        <Button 
                          onClick={() => startWaiverFlow(player)}
                          className="btn-primary"
                          data-testid={`sign-waiver-btn-${player.player_id}`}
                        >
                          {status.action === 'renew' ? 'Renew Waiver' : 'Sign Waiver'}
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadWaiverPdf(player.player_id)}
                            data-testid={`download-pdf-btn-${player.player_id}`}
                          >
                            <Download size={16} className="mr-1" /> PDF
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={handlePrint}
                          >
                            Print
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      
      {/* COPPA Compliance Notice */}
      <div className="px-4 pb-6">
        <Card className="bg-muted/30 border-border">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <Shield size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Privacy & Compliance</p>
                <p>
                  Stay On Hoops, operated by Intuitive Mentors Organization, complies with COPPA 
                  (Children&apos;s Online Privacy Protection Act) and California CCPA/CPRA. We collect 
                  only necessary information for program operations with verified parental consent.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <MobileNavigation userRole="parent" />
    </div>
  );
}
